import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, CheckCircle2, Lock, Camera, Radio, Thermometer, SprayCan } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import QRScannerModal from "@/components/QRScannerModal";
import MobileNumpad, { useIsMobile } from "@/components/MobileNumpad";
import CleaningLogForm from "@/pages/CleaningLogForm";
import MaintenanceLogForm from "@/pages/MaintenanceLogForm";
import DeviationPhotoPicker from "@/components/DeviationPhotoPicker";
import TemperatureOcrField from "@/components/TemperatureOcrField";

/**
 * Landing page after a successful QR scan. Equipment + site + facility
 * are locked — operator only enters value + notes (and root-cause if out
 * of range).
 */
export default function EquipmentScanLanding() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState(state || null);
  const [loading, setLoading] = useState(!state);
  const [err, setErr] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showRescan, setShowRescan] = useState(false);
  const [showDevModal, setShowDevModal] = useState(false);
  const [rootCause, setRootCause] = useState("");
  const [corrective, setCorrective] = useState("");
  const [immediate, setImmediate] = useState("");
  const [detectedAt, setDetectedAt] = useState("");
  const [devPhotos, setDevPhotos] = useState([]);
  const [photoErr, setPhotoErr] = useState("");
  const [progress, setProgress] = useState(null);
  const [mode, setMode] = useState(null);   // null = chooser, "temp" | "clean" | "maint"
  const isMobile = useIsMobile();
  // Allow `?keypad=1` to force the custom keypad even on desktop — useful
  // for touchscreen kiosks and for QA verification on dev tools.
  const forceKeypad = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("keypad") === "1";
  const useCustomPad = isMobile || forceKeypad;
  const [showKeypad, setShowKeypad] = useState(false);
  const valueRef = useRef(null);
  // OCR trail — populated when the operator uses the Snap-thermometer flow.
  const [ocr, setOcr] = useState(null); // { photo_path, parsed_value, confidence }

  // Fetch today's progress for this equipment so staff see "x/N done today".
  // Exception-mode (IoT logger) units have no scheduled reading target, so we
  // skip the progress pill entirely for them.
  useEffect(() => {
    const eid = data?.equipment?.id;
    if (!eid) return;
    if (data?.equipment?.monitoring_mode === "exception") { setProgress(null); return; }
    (async () => {
      try {
        const { data: p } = await api.get("/readings/equipment-progress", { params: { equipment_id: eid } });
        setProgress(p);
      } catch { setProgress(null); }
    })();
  }, [data?.equipment?.id, data?.equipment?.monitoring_mode, success]);

  // Fallback: deep-link to /app/scan/equipment/:id without state — re-resolve.
  useEffect(() => {
    if (data) return;
    (async () => {
      try {
        // Re-encode opaque token from the equipment id and resolve.
        const json = JSON.stringify({ t: "eq", id });
        const b64 = btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        const { data: r } = await api.post("/scan/resolve", { token: `VBE1:${b64}` });
        setData(r);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || "Unable to load this equipment.");
      } finally { setLoading(false); }
    })();
  }, [id, data]);

  // For non-temperature-monitored equipment (e.g. Band Saw), skip the
  // mode chooser and drop the user straight onto the maintenance log
  // form — that's the only capture flow that makes sense for it.
  //
  // Vehicles are the exception: Delivery Truck (no range) and Cold Truck
  // (range) both flow through /app/vehicle-scan which knows how to
  // render the cleanliness tab (delivery) or the temperature-vs-clean
  // chooser (cold). Redirect there rather than showing the generic
  // maintenance / temperature capture UI.
  useEffect(() => {
    if (!data || mode !== null) return;
    const typeName = (data.type?.name || "").toLowerCase();
    if (typeName === "delivery truck" || typeName === "cold truck") {
      navigate(`/app/vehicle-scan/${data.equipment?.id || id}`, { replace: true });
      return;
    }
    const range = data.type?.has_operating_range && data.type?.min_value !== null
      && data.type?.max_value !== null && data.type?.min_value !== undefined
      && data.type?.max_value !== undefined;
    if (!range) setMode("maint");
  }, [data, mode, id, navigate]);

  if (loading) return <div className="p-6 opacity-70" data-testid="scan-loading">Loading…</div>;
  if (err && !data) {
    return (
      <div className="max-w-lg vb-fade-in p-6" data-testid="scan-error">
        <div className="vb-pill err mb-3"><AlertTriangle size={14} /> {err}</div>
        <button className="vb-btn" onClick={() => setShowRescan(true)} data-testid="scan-rescan-btn">
          <Camera size={16} /> Scan again
        </button>
        {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      </div>
    );
  }

  const eq = data.equipment;
  const t = data.type || {};
  const site = data.site || {};
  const facility = data.facility || {};
  const unit = t.parameter_unit || "";
  const paramName = t.parameter_name || "Value";
  const minV = t.min_value;
  const maxV = t.max_value;
  const hasRange = !!t.has_operating_range && minV !== null && maxV !== null && minV !== undefined && maxV !== undefined;
  const isException = eq.monitoring_mode === "exception";
  const isDev = hasRange && value !== "" && (Number(value) < minV || Number(value) > maxV);
  // OCR only makes sense for temperature parameters (°C / °F).
  const isTempParam = hasRange && /^°?[CF]$/i.test((unit || "").trim());

  const trySubmit = (e) => {
    e.preventDefault(); setErr("");
    if (!hasRange) { setErr("This equipment has no operating range — ask your manager to configure operating parameters on the equipment type."); return; }
    if (isDev) { setShowDevModal(true); return; }
    doSubmit();
  };

  const doSubmit = async () => {
    setBusy(true); setErr(""); setPhotoErr("");
    try {
      const ocrPayload = ocr ? {
        ocr_photo_path: ocr.photo_path,
        ocr_parsed_value: ocr.parsed_value,
        ocr_confidence: ocr.confidence,
        ocr_edited: Math.abs(Number(value) - Number(ocr.parsed_value)) > 0.05,
      } : {};
      const { data: r } = await api.post("/readings/by-equipment", {
        equipment_id: eq.id,
        value: Number(value),
        notes,
        root_cause: rootCause || undefined,
        corrective_action: corrective || undefined,
        immediate_action_taken: immediate || undefined,
        detected_at: isException ? new Date(detectedAt || new Date().toISOString().slice(0, 10)).toISOString() : undefined,
        ...ocrPayload,
      });
      // Attach any evidence photos AFTER the reading is created (endpoint
      // needs the reading id). Photo upload failures are soft — the
      // reading itself still saves so compliance is not blocked.
      const rid = r.reading?.id;
      if (rid && devPhotos.length && r.reading?.is_deviation) {
        const failed = [];
        for (const f of devPhotos) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/readings/${rid}/photo`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch {
            failed.push(f.name || "photo");
          }
        }
        if (failed.length) {
          setPhotoErr(`Reading saved, but ${failed.length} photo${failed.length > 1 ? "s" : ""} failed to upload: ${failed.join(", ")}`);
        }
      }
      setSuccess(r.reading);
      setShowDevModal(false);
      setValue(""); setNotes(""); setRootCause(""); setCorrective(""); setImmediate("");
      setDetectedAt(""); setDevPhotos([]); setOcr(null);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (success) {
    return (
      <div className="max-w-lg vb-fade-in p-6" data-testid="scan-success">
        <div className="vb-pill ok mb-4"><CheckCircle2 size={14} /> Saved · {success.value}{success.unit} · {success.is_deviation ? "deviation logged" : "within range — no action required"}</div>
        {photoErr && <div className="vb-pill warn mb-4" data-testid="scan-photo-warn">{photoErr}</div>}
        <h2 className="vb-heading text-2xl mb-2" style={{ color: "var(--vb-text-2)" }}>Reading recorded</h2>
        <p className="opacity-70 mb-6 text-sm">{eq.name} · {site.name}{facility.name ? ` · ${facility.name}` : ""}</p>
        <div className="flex gap-3 flex-wrap">
          <button className="vb-btn" onClick={() => setShowRescan(true)} data-testid="scan-next-btn"><Camera size={16} /> Scan next equipment</button>
          <button className="vb-btn ghost" onClick={() => navigate("/app")} data-testid="scan-go-home">Done</button>
        </div>
        {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      </div>
    );
  }

  // Mode chooser — after scanning equipment, the operator picks the task.
  if (mode === null) {
    return (
      <div className="max-w-lg vb-fade-in" data-testid="scan-mode-chooser">
        <div className="vb-card p-5 mb-4">
          <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Scanned equipment</div>
          <div className="vb-heading text-2xl mb-1" style={{ color: "var(--vb-text-2)" }} data-testid="scan-chooser-eq-name">{eq.name}</div>
          <div className="text-sm opacity-80">
            <Lock size={12} className="inline-block mr-1 opacity-60" />
            {site.name || "—"}{facility?.name ? ` · ${facility.name}` : ""}{eq.serial_number ? ` · S/N ${eq.serial_number}` : ""}
          </div>
        </div>
        <p className="text-sm opacity-70 mb-3 px-1">What would you like to do?</p>
        <div className="grid gap-3">
          <button className="vb-card p-5 text-left flex items-center gap-4 vb-hover-lift"
            style={{ cursor: "pointer" }} onClick={() => setMode("temp")} data-testid="scan-choose-temperature">
            <Thermometer size={28} style={{ color: "var(--vb-text-2)" }} />
            <div>
              <div className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Record temperature</div>
              <div className="text-sm opacity-70">Log a CCP temperature reading for this unit.</div>
            </div>
          </button>
          <button className="vb-card p-5 text-left flex items-center gap-4 vb-hover-lift"
            style={{ cursor: "pointer" }} onClick={() => setMode("clean")} data-testid="scan-choose-cleaning">
            <SprayCan size={28} style={{ color: "var(--vb-text-2)" }} />
            <div>
              <div className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>Log cleaning</div>
              <div className="text-sm opacity-70">Review the cleaning protocol and confirm it's done.</div>
            </div>
          </button>
        </div>
        <button type="button" className="vb-btn ghost mt-4" onClick={() => setShowRescan(true)} data-testid="scan-chooser-rescan">
          <Camera size={16} /> Scan a different code
        </button>
        {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      </div>
    );
  }

  if (mode === "maint") {
    return (
      <>
        <MaintenanceLogForm
          equipmentId={eq.id}
          equipmentName={eq.name}
          siteName={site.name}
          facilityName={facility.name}
          serialNumber={eq.serial_number}
          onBack={hasRange ? () => setMode(null) : undefined}
          onRescan={() => setShowRescan(true)}
          onHome={() => navigate("/app")}
        />
        {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      </>
    );
  }

  if (mode === "clean") {
    return (
      <>
        <CleaningLogForm
          equipmentId={eq.id}
          equipmentName={eq.name}
          siteName={site.name}
          onBack={() => setMode(null)}
          onRescan={() => setShowRescan(true)}
          onHome={() => navigate("/app")}
        />
        {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      </>
    );
  }

  return (
    <div className="max-w-lg vb-fade-in" data-testid="scan-landing">
      <button onClick={() => navigate(-1)} className="vb-btn ghost mb-3" data-testid="scan-back"><ArrowLeft size={14} /> Back</button>

      <div className="vb-card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Scanned equipment</div>
        <div className="vb-heading text-2xl mb-1" style={{ color: "var(--vb-text-2)" }} data-testid="scan-eq-name">{eq.name}</div>
        {isException && (
          <div className="vb-pill warn mb-1" data-testid="scan-iot-badge" style={{ marginTop: 2 }}>
            <Radio size={13} /> External IoT logger — deviation capture
          </div>
        )}
        <div className="text-sm opacity-80" data-testid="scan-meta">
          <Lock size={12} className="inline-block mr-1 opacity-60" />
          {site.name || "—"}
          {facility?.name ? ` · ${facility.name}` : ""}
          {eq.serial_number ? ` · S/N ${eq.serial_number}` : ""}
        </div>
        {hasRange ? (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <div className="vb-pill" data-testid="scan-range">{paramName} range: <strong>{minV}{unit} – {maxV}{unit}</strong></div>
            {progress && (
              <div className={`vb-pill ${progress.complete ? "ok" : "warn"}`} data-testid="scan-progress">
                <CheckCircle2 size={12} /> {progress.count}/{progress.target} done today
              </div>
            )}
          </div>
        ) : (
          <div className="vb-pill err mt-3" data-testid="scan-no-range">
            <AlertTriangle size={14} /> No operating range set. Ask your manager to configure this equipment type.
          </div>
        )}
        <button type="button" className="vb-btn ghost mt-3" onClick={() => setShowRescan(true)} data-testid="scan-wrong-btn">
          Tap to rescan
        </button>
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="scan-form-error">{err}</div>}

      <form onSubmit={trySubmit} className="vb-card p-5 grid gap-4">
        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
            Measured value {unit && `(${unit})`}
          </label>
          <div className="relative">
            <input
              ref={valueRef}
              className="vb-input" type="text"
              inputMode={useCustomPad ? "none" : "decimal"}
              pattern="-?(\d+\.?\d*|\.\d+)" required
              readOnly={useCustomPad}
              value={value} onChange={(e) => setValue(e.target.value)}
              onFocus={() => {
                if (!useCustomPad || !hasRange) return;
                setShowKeypad(true);
                // Scroll the field to the top once the keypad has taken its
                // space at the bottom, so the user can still see their
                // reading update as they tap keys.
                setTimeout(() => valueRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 60);
              }}
              onClick={() => {
                if (!useCustomPad || !hasRange) return;
                setShowKeypad(true);
                setTimeout(() => valueRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 60);
              }}
              placeholder={hasRange ? `Enter ${paramName.toLowerCase()} in ${unit}` : "Enter measurement"}
              data-testid="scan-value"
              style={{ paddingRight: unit ? 72 : undefined }}
              disabled={!hasRange}
            />
            {unit && <span className="absolute top-1/2 right-3 -translate-y-1/2 text-xs font-semibold px-2 py-0.5 rounded" style={{ color: "var(--vb-text-2)", background: "var(--vb-bg)", pointerEvents: "none" }}>{unit}</span>}
          </div>
          {isDev && (
            <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: "var(--vb-error)" }}>
              <AlertTriangle size={16} /> Out of range — corrective action will be required.
            </div>
          )}
          {isException && !isDev && value !== "" && (
            <div className="mt-2 flex items-center gap-2 text-sm" data-testid="scan-inrange-ok" style={{ color: "var(--vb-accent)" }}>
              <CheckCircle2 size={16} /> Within operating range — no action required.
            </div>
          )}
          {isTempParam && (
            <div className="mt-3">
              <TemperatureOcrField
                context="ccp"
                minC={Number(minV)}
                maxC={Number(maxV)}
                onConfirm={(r) => {
                  setValue(r.temp_c.toString());
                  setOcr({ photo_path: r.ocr_photo_path,
                           parsed_value: r.ocr_parsed_value,
                           confidence: r.ocr_confidence });
                }}
                onFallbackToManual={() => setOcr(null)}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Notes (optional)</label>
          <textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="scan-notes" />
        </div>

        <button type="submit" className="vb-btn" disabled={busy || value === "" || !hasRange} data-testid="scan-submit">
          {busy ? "Saving…" : isDev ? (isException ? "Log deviation" : "Continue with deviation") : "Submit reading"}
        </button>
      </form>

      {showDevModal && hasRange && createPortal((
        <div className="vb-modal-bg" data-testid="scan-dev-modal">
          <div className="vb-modal">
            <div className="vb-deviation-banner">
              <AlertTriangle size={20} />
              {isException
                ? <> Deviation — {value}{unit} is outside {minV}–{maxV}{unit}</>
                : <> Deviation — {value}{unit} is outside {minV}–{maxV}{unit}</>}
            </div>
            <h2>{isException ? "Record deviation & corrective action" : "Document corrective action"}</h2>
            <p className="text-sm opacity-80 mb-4">ISO 22000 requires immediate corrective action plus root-cause documentation.</p>
            {isException && (
              <div className="mb-3">
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Date detected</label>
                <input type="date" className="vb-input" value={detectedAt || new Date().toISOString().slice(0, 10)}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDetectedAt(e.target.value)} data-testid="scan-dev-detected-at" />
              </div>
            )}
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Root cause *</label>
            <textarea className="vb-input mb-3" rows={2} required value={rootCause} onChange={(e) => setRootCause(e.target.value)} data-testid="scan-dev-root" />
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Immediate action taken</label>
            <textarea className="vb-input mb-3" rows={2} value={immediate} onChange={(e) => setImmediate(e.target.value)} data-testid="scan-dev-immediate" />
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Corrective action *</label>
            <textarea className="vb-input mb-4" rows={3} required value={corrective} onChange={(e) => setCorrective(e.target.value)} data-testid="scan-dev-corrective" />
            <div className="mb-4">
              <DeviationPhotoPicker files={devPhotos} onChange={setDevPhotos} disabled={busy} />
            </div>
            <div className="flex gap-3 justify-end">
              <button type="button" className="vb-btn ghost" onClick={() => setShowDevModal(false)}>Cancel</button>
              <button type="button" className="vb-btn warn" onClick={doSubmit} disabled={busy || !rootCause || !corrective} data-testid="scan-dev-submit">
                {busy ? "Submitting…" : isException ? "Log deviation" : "Submit deviation"}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {showRescan && <QRScannerModal onClose={() => setShowRescan(false)} />}
      {showKeypad && (
        <MobileNumpad
          value={value}
          unit={unit}
          onChange={setValue}
          onClose={() => setShowKeypad(false)}
        />
      )}
    </div>
  );
}
